import type { FC } from "react"
import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import type { AlertSettingType, AlertSettingUpsertForm } from "types/alerts.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import { useAddPowerlineAlertSettingMutation } from "features/api"
import { formatMutationParams } from "helpers/utils/alerts"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import AlertSettingForm from "../AlertSettingForm"
import { getNewAlertSettingDefaultValues } from "../thresholds"

interface AddPowerlineAlertSettingsProps {
  types: AlertSettingType[]
  onClose: () => void
  powerlineId: number
}

const AddPowerlineAlertSetting: FC<AddPowerlineAlertSettingsProps> = ({
  types,
  onClose,
  powerlineId,
}) => {
  const { t, i18n } = useTranslation()
  const orgId = useSelector(selectCurrentOrgId)
  const methods = useForm<AlertSettingUpsertForm>({
    mode: "all",
    defaultValues: {
      type: "",
    },
  })
  const { reset, getValues, watch } = methods
  const selectedTypeName = watch("type")

  const selectedType = useMemo(
    () => types?.find((type) => type.name === selectedTypeName),
    [types, selectedTypeName],
  )

  const [addPowerlineAlertSetting, { isLoading }] = useAddPowerlineAlertSettingMutation()

  useEffect(() => {
    reset(getNewAlertSettingDefaultValues(getValues("type"), selectedType))
  }, [selectedType, reset, getValues])

  const onSubmit = async (data: AlertSettingUpsertForm) => {
    const defaultEnabledData = {
      ...data,
      active: true,
    }
    const formattedData = formatMutationParams(
      defaultEnabledData,
      selectedType,
      i18n.resolvedLanguage || "en",
    )
    snackbarMutation({
      mutation: addPowerlineAlertSetting({
        powerlineId,
        org_id: Number(orgId),
        ...formattedData,
      }).unwrap(),
      getErrorMessage: buildGetErrorMessage(
        t("error.CREATING_ITEM", { item: t("alert_settings.ALERT_SETTING") }),
      ),
      getSuccessMessage: () =>
        t("success.CREATING_ITEM", {
          item: t("alert_settings.ALERT_SETTING"),
          count: 1,
          context: "female",
        }),
    })
      .then(() => {
        onClose()
      })
      .catch(() => {})
  }

  return (
    <AlertSettingForm
      entity="powerline"
      entityId={powerlineId}
      isLoading={isLoading}
      title="alerts.NEW_ALERT_SETTING"
      onSubmit={methods.handleSubmit(onSubmit)}
      methods={methods}
      types={types}
      selectedTypeName={selectedTypeName}
      onClose={onClose}
      buttonText="generic.CREATE"
    />
  )
}

export default AddPowerlineAlertSetting
